import React from 'react';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import MoreVertRounded from '@mui/icons-material/MoreVertRounded';
import { Play } from 'lucide-react';
import { coverImageUrl, coverReferrerPolicy } from '../coverReferrerPolicy.ts';

/**
 * NyomiriCard: Distinctive Yomiri Liquid Glass Poster Card (Netflix / Hulu OTT Edition)
 * Desktop width: 176px, Aspect ratio: 2:3, Radius: 10px.
 * Features:
 * - Floating glass pill badges with glowing dot accents & Top 10 badges
 * - Cinematic Scrim gradient overlay on poster
 * - Quick-action OTT Play button revealing smoothly on hover & focus
 * - Inner zoom on poster image without shifting layout
 * - Smooth 3D lift & cherry ambient glow on hover & TV D-Pad focus
 * - Clean subtitle with status dot
 */

export interface NyomiriCardProps {
  title: string;
  coverUrl?: string | null;
  subtitle?: string | null;
  format?: string | null;
  averageScore?: number | null;
  progress?: number | null; // 0..100
  href?: string;
  onClick?: (e?: React.MouseEvent<HTMLElement>) => void;
  onContextMenu?: (e: React.MouseEvent<HTMLElement>) => void;
  onOpenMenu?: (e: React.MouseEvent<HTMLElement>) => void;
  className?: string;
}

const PILL_BADGE = {
  position: 'absolute',
  top: 8,
  borderRadius: '5px',
  bgcolor: 'rgba(9, 9, 14, 0.88)',
  backdropFilter: 'blur(8px)',
  color: '#ffffff',
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: '0.06em',
  lineHeight: '12px',
  px: 0.9,
  py: 0.4,
  display: 'inline-flex',
  alignItems: 'center',
  gap: '5px',
  userSelect: 'none',
  border: '1px solid rgba(255, 255, 255, 0.14)',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.7)',
  textTransform: 'uppercase',
  zIndex: 3,
} as const;

export const NyomiriCard = React.memo(function NyomiriCard({
  title,
  coverUrl,
  subtitle,
  format,
  averageScore,
  progress,
  href,
  onClick,
  onContextMenu,
  onOpenMenu,
  className = '',
}: NyomiriCardProps) {
  const formattedFormat = format && format !== 'TV' ? format.replace(/_/g, ' ') : (format === 'TV' ? 'ANIME' : null);
  const isTopRated = averageScore != null && averageScore >= 82;

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    if (href && e.button === 0 && !e.ctrlKey && !e.metaKey && !e.shiftKey && !e.altKey) {
      e.preventDefault();
    }
    onClick?.(e);
  };

  const handleContextMenu = (e: React.MouseEvent<HTMLElement>) => {
    if (onContextMenu) {
      e.preventDefault();
      e.stopPropagation();
      onContextMenu(e);
    }
  };

  return (
    <ButtonBase
      component={href ? 'a' : 'button'}
      href={href}
      className={`nyomiri-card ${className}`}
      onClick={handleClick}
      onContextMenu={handleContextMenu}
      aria-label={title}
      sx={{
        width: 176,
        minWidth: 176,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        justifyContent: 'flex-start',
        textAlign: 'left',
        scrollSnapAlign: 'start',
        borderRadius: '10px',
        bgcolor: 'transparent',
        textDecoration: 'none',
        color: 'inherit',
        p: 0,
        m: 0,
        touchAction: 'manipulation',
        WebkitTapHighlightColor: 'transparent',
        transition: 'transform 0.25s cubic-bezier(0.16, 1, 0.3, 1)',
        outline: 'none',
        position: 'relative',
        '&:focus-visible, &:focus, &[data-focused="true"]': {
          transform: 'scale(1.06) translateY(-6px)',
          zIndex: 10,
        },
        '&:focus-visible .nyomiri-card-cover, &:focus .nyomiri-card-cover, &[data-focused="true"] .nyomiri-card-cover': {
          borderColor: '#ffffff',
          boxShadow: '0 0 0 3px var(--accent-500, #ff2e63), 0 20px 40px rgba(0, 0, 0, 0.9), 0 0 28px rgba(255, 46, 99, 0.5)',
        },
        '&:focus-visible .nyomiri-card-img, &:focus .nyomiri-card-img, &[data-focused="true"] .nyomiri-card-img': {
          transform: 'scale(1.08)',
          filter: 'brightness(1.08) contrast(1.04)',
        },
        '&:focus-visible .nyomiri-card-overlay, &:focus .nyomiri-card-overlay, &[data-focused="true"] .nyomiri-card-overlay': {
          opacity: 1,
        },
        '&:focus-visible .nyomiri-card-play-btn, &:focus .nyomiri-card-play-btn, &[data-focused="true"] .nyomiri-card-play-btn': {
          opacity: 1,
          transform: 'scale(1)',
        },
        '&:focus-visible .nyomiri-card-menu-btn, &:focus .nyomiri-card-menu-btn, &[data-focused="true"] .nyomiri-card-menu-btn': {
          opacity: 1,
          transform: 'scale(1)',
        },
        '&:hover': {
          transform: 'scale(1.05) translateY(-5px)',
          zIndex: 9,
        },
        '&:hover .nyomiri-card-cover': {
          borderColor: 'rgba(255, 46, 99, 0.75)',
          boxShadow: '0 20px 38px -6px rgba(0, 0, 0, 0.85), 0 0 22px rgba(255, 46, 99, 0.4)',
        },
        '&:hover .nyomiri-card-img': {
          transform: 'scale(1.07)',
          filter: 'brightness(1.06) contrast(1.03)',
        },
        '&:hover .nyomiri-card-overlay': {
          opacity: 1,
        },
        '&:hover .nyomiri-card-play-btn': {
          opacity: 1,
          transform: 'scale(1)',
        },
        '&:hover .nyomiri-card-menu-btn': {
          opacity: 1,
          transform: 'scale(1)',
        },
        '&:active': {
          transform: 'scale(0.98)',
        },
      }}
    >
      {/* 2:3 Cover Container - Crisp OTT Poster with Rounded Radius */}
      <Box
        className="nyomiri-card-cover"
        sx={{
          position: 'relative',
          width: '100%',
          aspectRatio: '2/3',
          overflow: 'hidden',
          borderRadius: '10px',
          bgcolor: '#0a0a0f',
          border: '1.5px solid rgba(255, 255, 255, 0.08)',
          boxShadow: '0 4px 18px rgba(0, 0, 0, 0.6)',
          transition: 'border-color 0.25s ease, box-shadow 0.25s ease',
        }}
      >
        {coverUrl ? (
          <Box
            component="img"
            className="nyomiri-card-img"
            src={coverImageUrl(coverUrl)}
            referrerPolicy={coverReferrerPolicy(coverUrl)}
            alt=""
            loading="lazy"
            decoding="async"
            sx={{
              display: 'block',
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              transform: 'scale(1)',
              transition: 'transform 0.35s cubic-bezier(0.16, 1, 0.3, 1), filter 0.35s cubic-bezier(0.16, 1, 0.3, 1)',
            }}
          />
        ) : (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'grid',
              placeItems: 'center',
              bgcolor: 'rgba(255, 255, 255, 0.04)',
              color: 'var(--text-muted)',
              fontSize: 12,
              backdropFilter: 'blur(12px)',
            }}
          >
            Sin portada
          </Box>
        )}

        {/* Cinematographic Bottom Scrim Gradient Overlay */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(to top, rgba(5, 5, 8, 0.8) 0%, rgba(5, 5, 8, 0.25) 30%, transparent 60%)',
            pointerEvents: 'none',
            zIndex: 1,
          }}
        />

        {/* Interactive Hover Vignette / Quick Action Overlay */}
        <Box
          className="nyomiri-card-overlay"
          sx={{
            position: 'absolute',
            inset: 0,
            bgcolor: 'rgba(5, 5, 10, 0.45)',
            backdropFilter: 'blur(2px)',
            opacity: 0,
            transition: 'opacity 0.25s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2,
            pointerEvents: 'none',
          }}
        >
          {/* Netflix-Style Floating Center Play Button */}
          <Box
            className="nyomiri-card-play-btn"
            sx={{
              width: 44,
              height: 44,
              borderRadius: '50%',
              bgcolor: 'var(--accent-500, #ff2e63)',
              color: '#ffffff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 0 20px rgba(255, 46, 99, 0.7), 0 4px 12px rgba(0, 0, 0, 0.5)',
              opacity: 0,
              transform: 'scale(0.8)',
              transition: 'opacity 0.25s ease, transform 0.25s cubic-bezier(0.16, 1, 0.3, 1)',
              paddingLeft: '2px', // visual optical centering for play icon
            }}
          >
            <Play size={20} fill="#ffffff" color="#ffffff" />
          </Box>
        </Box>

        {/* Format Pill Badge: Top-Left with Cherry Indicator */}
        {formattedFormat && (
          <Box component="span" sx={{ ...PILL_BADGE, left: 8 }}>
            <Box
              component="span"
              sx={{
                width: 5,
                height: 5,
                borderRadius: '50%',
                bgcolor: 'var(--accent-500, #ff2e63)',
                boxShadow: '0 0 6px var(--accent-500, #ff2e63)',
              }}
            />
            {formattedFormat}
          </Box>
        )}

        {/* Score Pill Badge: Top-Right with Score Tint or Top 10 distinction */}
        {averageScore != null && (
          <Box
            component="span"
            sx={{
              ...PILL_BADGE,
              right: 8,
              fontVariantNumeric: 'tabular-nums',
              ...(isTopRated ? {
                borderColor: 'rgba(255, 46, 99, 0.4)',
                bgcolor: 'rgba(20, 8, 14, 0.92)',
              } : {}),
            }}
          >
            <Box
              component="span"
              sx={{
                width: 5,
                height: 5,
                borderRadius: '50%',
                bgcolor: averageScore >= 75 ? (isTopRated ? '#ff2e63' : '#10b981') : '#f59e0b',
                boxShadow: `0 0 6px ${averageScore >= 75 ? (isTopRated ? '#ff2e63' : '#10b981') : '#f59e0b'}`,
              }}
            />
            {averageScore}%
          </Box>
        )}

        {/* Context Menu Trigger for Continue cards */}
        {onOpenMenu && (
          <IconButton
            className="nyomiri-card-menu-btn"
            size="small"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onOpenMenu(e);
            }}
            aria-label="Opciones de obra"
            sx={{
              position: 'absolute',
              bottom: 8,
              right: 8,
              width: 30,
              height: 30,
              bgcolor: 'rgba(10, 10, 16, 0.82)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.16)',
              color: '#ffffff',
              opacity: 0,
              transform: 'scale(0.85)',
              zIndex: 4,
              transition: 'opacity 0.2s ease, transform 0.2s ease, background-color 0.2s ease',
              '&:hover': {
                bgcolor: 'rgba(255, 46, 99, 0.9)',
                borderColor: '#ff2e63',
              },
            }}
          >
            <MoreVertRounded sx={{ fontSize: 16 }} />
          </IconButton>
        )}

        {/* Progress bar: Netflix-like Liquid Cherry glow line at bottom edge */}
        {progress != null && progress > 0 && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: 4,
              bgcolor: 'rgba(0, 0, 0, 0.75)',
              zIndex: 3,
            }}
          >
            <Box
              sx={{
                height: '100%',
                width: `${Math.min(100, Math.max(0, progress))}%`,
                bgcolor: 'var(--accent-500, #ff2e63)',
                boxShadow: '0 0 8px rgba(255, 46, 99, 0.9)',
              }}
            />
          </Box>
        )}
      </Box>

      {/* Title below cover - Crisp OTT Editorial Typography */}
      <Typography
        component="span"
        sx={{
          mt: 1.1,
          fontSize: 13,
          lineHeight: '18px',
          fontWeight: 700,
          letterSpacing: '-0.01em',
          color: 'var(--text-primary, #ffffff)',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          transition: 'color 0.2s ease',
        }}
      >
        {title}
      </Typography>

      {/* Subtitle with Glowing Cherry Status Dot & Subtle OTT metadata */}
      {subtitle && (
        <Box
          component="span"
          sx={{
            mt: 0.35,
            fontSize: 11.5,
            lineHeight: 1.25,
            color: 'var(--text-muted, #94a3b8)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.6,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          <Box
            component="span"
            sx={{
              width: 4,
              height: 4,
              borderRadius: '50%',
              bgcolor: 'var(--accent-500, #ff2e63)',
              flexShrink: 0,
              boxShadow: '0 0 6px var(--accent-500, #ff2e63)',
            }}
          />
          <Typography
            component="span"
            sx={{
              fontSize: 'inherit',
              color: 'inherit',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {subtitle}
          </Typography>
        </Box>
      )}
    </ButtonBase>
  );
});
